import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaUserCircle, FaInfoCircle, FaQuestionCircle, FaPlusCircle, FaHistory, FaHeadset, FaPlay, FaTimes, FaEnvelope, FaInstagram, FaWhatsapp, FaYoutube, FaCalendarAlt } from 'react-icons/fa';
import Modal from '../Modal/Modal';
import { toggleSidebar, addNotification } from '../../redux/slices/uiSlice';
import { loadSettings } from '../../redux/slices/settingsSlice';
import './Navbar.scss';
import PlanCard from '../PlanCard/PlanCard';
import PaymentHistoryModal from '../PaymentHistoryModal/PaymentHistoryModal';
import SyncButton from '../SyncStatus/SyncButton';
import SyncCenter from '../SyncStatus/SyncCenter';
import { showToast } from '../../utils/toast';

import { GoldStar, SilverStar } from '../PlanCard/PlanCard';
import NotificationPanel from '../NotificationPanel';


const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useSelector((state) => state.auth);
  const { profile } = useSelector((state) => state.settings);
  const siteSettings = useSelector((state) => state.settings.siteSettings || {});

  const [showTutorial, setShowTutorial] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);
  // Help/contact form state
  const [helpForm, setHelpForm] = React.useState({ name: '', email: '', subject: '', message: '' });
  const [helpErrors, setHelpErrors] = React.useState({});
  const [helpSubmitting, setHelpSubmitting] = React.useState(false);
  const [helpSuccess, setHelpSuccess] = React.useState(false);
  const [showPlan, setShowPlan] = React.useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = React.useState(false);
  const notifications = useSelector((state) => state.ui.notifications || []);

  // Offline state
  const [isOnline, setIsOnline] = React.useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Pre-fill help form with user data
  React.useEffect(() => {
    if (showHelp && (user || profile)) {
      setHelpForm(prev => ({
        ...prev,
        name: prev.name || profile?.fullName || user?.name || '',
        email: prev.email || profile?.email || user?.email || ''
      }));
    }
  }, [showHelp, user, profile]);

  // logout navigation handled from settings page; keep action here if needed later

  // Helper: check if name is valid (not empty, not dummy value)
  const isValidName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    if (!trimmed) return false;
    // Filter out common dummy/test names (case-insensitive)
    const dummyNames = ['user', 'test', 'demo', 'admin', 'guest', 'unknown', 'null', 'undefined', 'name'];
    return !dummyNames.includes(trimmed.toLowerCase());
  };

  // Use profile data if available; prefer uploaded `avatar` (absolute URL)
  const displayName = (isValidName(profile?.fullName) && profile.fullName) || 'Unknown';
  const displayAvatar = profile?.avatar || profile?.avatarPreview || user?.avatar || null;

  // Privileged roles (admin/superadmin) always have active gold plan - no expiry
  const userRole = String(user?.role || profile?.role || '').toLowerCase();
  const isPrivilegedUser = userRole === 'admin' || userRole === 'superadmin';

  // Plan logic
  const rawPlan = isPrivilegedUser ? 'gold' : ((profile && (profile.plan || profile.subscription?.tier)) || user?.plan || 'none');
  const planTier = String(rawPlan || 'none').toLowerCase();
  const isGold = planTier.includes('gold');
  const isSilver = planTier.includes('silver');
  const hasPaidPlan = isGold || isSilver;
  const planTitle = planTier === 'none' || planTier === 'free' 
    ? 'Free Trial' 
    : planTier.charAt(0).toUpperCase() + planTier.slice(1);
  
  // Trial period constant (15 days)
  const TRIAL_DAYS = 15;

  // Live clock so hrs/mins/secs display stays accurate (re-render every second)
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  
  const remainingDays = React.useMemo(() => {
    // Admin/superadmin never expire
    if (isPrivilegedUser) return 9999;

    // Canonical expiry: backend/utils/subscription.js#getSubscriptionStatus
    //   duration = (initialPlanDays ?? 15/365) + planExtraDays (negative allowed)
    const resolveBaseDays = (isPaid) => {
      const snap = parseInt(profile?.initialPlanDays ?? user?.initialPlanDays ?? NaN, 10);
      if (Number.isFinite(snap) && snap > 0) return snap;
      return isPaid ? 365 : TRIAL_DAYS;
    };

    // For paid plans, prefer server-computed subscription (already includes planExtraDays)
    if (hasPaidPlan) {
      if (profile?.subscription?.daysRemaining !== undefined) {
        return Math.max(0, Number(profile.subscription.daysRemaining) || 0);
      }
      if (user?.subscription?.daysRemaining !== undefined) {
        return Math.max(0, Number(user.subscription.daysRemaining) || 0);
      }

      const planActivatedAt = profile?.planActivatedAt || user?.planActivatedAt;
      if (planActivatedAt) {
        const extraDays = parseInt(profile?.planExtraDays ?? user?.planExtraDays ?? 0, 10) || 0;
        const baseDays = resolveBaseDays(true);
        const activationDate = new Date(planActivatedAt);
        const expiryDate = new Date(activationDate);
        expiryDate.setDate(expiryDate.getDate() + baseDays + extraDays);

        const now = new Date(nowMs);
        const diff = expiryDate - now;
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
      } else if (profile?.subscription?.endDate) {
        const end = new Date(profile.subscription.endDate);
        const diff = end - new Date(nowMs);
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
      }
      return resolveBaseDays(true); // Default for paid plans
    }

    // For free/no plan users, prefer server-computed subscription
    if (profile?.subscription?.daysRemaining !== undefined) {
      return Math.max(0, Number(profile.subscription.daysRemaining) || 0);
    }
    if (user?.subscription?.daysRemaining !== undefined) {
      return Math.max(0, Number(user.subscription.daysRemaining) || 0);
    }
    const createdAt = user?.createdAt || profile?.createdAt;
    if (createdAt) {
      const extraDays = parseInt(profile?.planExtraDays ?? user?.planExtraDays ?? 0, 10) || 0;
      const baseDays = resolveBaseDays(false);
      const registrationDate = new Date(createdAt);
      const now = new Date(nowMs);
      const diffTime = now - registrationDate;
      const daysUsed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, baseDays + extraDays - daysUsed);
    }

    return resolveBaseDays(false); // Default trial days if no createdAt
  }, [profile, user, hasPaidPlan, isPrivilegedUser, nowMs]);

  // Canonical expire date for the plan modal history CTA.
  // Mirrors remainingDays logic but returns a Date (or null for lifetime).
  const planExpiryDate = React.useMemo(() => {
    if (isPrivilegedUser) return null; // lifetime access

    const resolveBaseDays = (isPaid) => {
      const snap = parseInt(profile?.initialPlanDays ?? user?.initialPlanDays ?? NaN, 10);
      if (Number.isFinite(snap) && snap > 0) return snap;
      return isPaid ? 365 : TRIAL_DAYS;
    };

    // Prefer server-computed expiry — same source as admin "Recent Registers"
    const serverExpires =
      profile?.subscription?.expiresAt ||
      user?.subscription?.expiresAt ||
      user?.planExpiresAt ||
      profile?.planExpiresAt ||
      null;
    if (serverExpires) {
      const parsed = new Date(serverExpires);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    const endDate = profile?.subscription?.endDate || user?.subscription?.endDate;
    if (endDate) {
      const parsed = new Date(endDate);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    if (hasPaidPlan) {
      const planActivatedAt = profile?.planActivatedAt || user?.planActivatedAt;
      if (planActivatedAt) {
        const activation = new Date(planActivatedAt);
        if (!Number.isNaN(activation.getTime())) {
          const extraDays = parseInt(profile?.planExtraDays ?? user?.planExtraDays ?? 0, 10) || 0;
          const expiry = new Date(activation);
          expiry.setDate(expiry.getDate() + resolveBaseDays(true) + extraDays);
          return expiry;
        }
      }
    }

    const createdAt = user?.createdAt || profile?.createdAt;
    if (createdAt) {
      const created = new Date(createdAt);
      if (!Number.isNaN(created.getTime())) {
        const extraDays = parseInt(profile?.planExtraDays ?? user?.planExtraDays ?? 0, 10) || 0;
        const expiry = new Date(created);
        expiry.setDate(expiry.getDate() + resolveBaseDays(hasPaidPlan) + extraDays);
        return expiry;
      }
    }

    return null;
  }, [profile, user, hasPaidPlan, isPrivilegedUser]);

  const planExpiryLabel = React.useMemo(() => {
    if (isPrivilegedUser) return 'Never expires';
    if (!planExpiryDate) return '—';
    try {
      return planExpiryDate.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (err) {
      return '—';
    }
  }, [planExpiryDate, isPrivilegedUser]);

  // Precise ms remaining (drives hrs/mins/secs display when < 1 day)
  const remainingMs = React.useMemo(() => {
    if (isPrivilegedUser) return Infinity;
    if (planExpiryDate) return planExpiryDate.getTime() - nowMs;
    if (Number.isFinite(remainingDays)) return remainingDays * 24 * 60 * 60 * 1000;
    return 0;
  }, [planExpiryDate, nowMs, remainingDays, isPrivilegedUser]);

  // Display: days if >= 1 day, hrs+mins+secs if < 1 day, mins+secs if < 1 hr
  const remainingDisplay = React.useMemo(() => {
    if (isPrivilegedUser) return { count: '∞', label: 'Lifetime Access', short: 'lifetime access' };
    const DAY = 24 * 60 * 60 * 1000;
    const HOUR = 60 * 60 * 1000;
    const MIN = 60 * 1000;
    const SEC = 1000;
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
      return { count: '0', label: 'Days Remaining', short: '0 days remaining' };
    }
    if (remainingMs >= DAY) {
      const d = Math.ceil(remainingMs / DAY);
      return {
        count: String(d),
        label: d === 1 ? 'Day Remaining' : 'Days Remaining',
        short: d === 1 ? '1 day remaining' : `${d} days remaining`,
      };
    }
    if (remainingMs >= HOUR) {
      const h = Math.floor(remainingMs / HOUR);
      const m = Math.floor((remainingMs % HOUR) / MIN);
      const s = Math.floor((remainingMs % MIN) / SEC);
      const count = `${h}h ${m}m ${s}s`;
      return {
        count,
        label: 'Hrs Mins Sec Left',
        short: `${count} remaining`,
      };
    }
    const m = Math.floor(remainingMs / MIN);
    const s = Math.floor((remainingMs % MIN) / SEC);
    const count = `${m}m ${s}s`;
    return {
      count,
      label: 'Mins Sec Left',
      short: `${count} remaining`,
    };
  }, [remainingMs, isPrivilegedUser]);

  const isPlanExpired = !isPrivilegedUser && remainingMs <= 0;
  const isPlanExpiringSoon = !isPrivilegedUser && remainingMs > 0 && remainingMs <= 7 * 24 * 60 * 60 * 1000;

  // Handle plan/trial expiration notifications
  React.useEffect(() => {
    if (isPrivilegedUser) return; // Admin/superadmin never get expiry notifications
    if (remainingDays === null || remainingDays > 15) return;

    // Milestones to notify (adjusted for 15-day trial)
    let milestone = null;
    if (remainingDays <= 15 && remainingDays > 7) milestone = 15;
    else if (remainingDays <= 7 && remainingDays > 3) milestone = 7;
    else if (remainingDays <= 3 && remainingDays > 0) milestone = remainingDays;
    else if (remainingDays <= 0) milestone = 0;

    if (milestone !== null) {
      const notifType = hasPaidPlan ? 'plan-expiry' : 'trial-expiry';
      const milestoneId = `${notifType}-${milestone}`;
      const alreadyNotified = notifications.some(n => n.milestoneId === milestoneId);

      if (!alreadyNotified) {
        const title = hasPaidPlan ? 'Plan Expiring Soon' : 'Free Trial Ending';
        const message = remainingDays <= 0 
          ? (hasPaidPlan 
              ? `Your ${planTitle} plan has expired. Please renew to continue using all features.`
              : 'Your free trial has ended. Subscribe to continue using all features.')
          : (hasPaidPlan 
              ? `Your ${planTitle} plan expires in ${remainingDays} days. Please renew to keep your features.`
              : `Your free trial ends in ${remainingDays} days. Subscribe to keep all features.`);
        
        dispatch(addNotification({
          title,
          message,
          type: 'billing',
          milestoneId: milestoneId,
          timestamp: new Date().toISOString()
        }));
      }
    }
  }, [remainingDays, planTier, planTitle, notifications, dispatch]);

  // Ensure settings are loaded so navbar shows correct profile immediately
  React.useEffect(() => {
    // Trigger loadSettings once on mount; the thunk will no-op or re-sync if already loaded
    dispatch(loadSettings());
  }, [dispatch]);

  // Listen for a global event that should open the Help modal
  React.useEffect(() => {
    const handler = (e) => {
      setShowHelp(true);
    };
    try {
      window.addEventListener('openHelp', handler);
    } catch (err) {
      // ignore if window is not available in some environments
    }
    return () => {
      try {
        window.removeEventListener('openHelp', handler);
      } catch (err) {}
    };
  }, []);

  // Close plan modal when a component dispatches the 'closePlan' event
  React.useEffect(() => {
    const closeHandler = () => setShowPlan(false);
    try {
      window.addEventListener('closePlan', closeHandler);
    } catch (err) {}
    return () => {
      try { window.removeEventListener('closePlan', closeHandler); } catch (err) {}
    };
  }, []);

  // Sync center modal state
  const [showSyncCenter, setShowSyncCenter] = React.useState(false);
  // Offline info modal state
  const [showOfflineInfo, setShowOfflineInfo] = React.useState(false);

  // Track online / offline status
  React.useEffect(() => {
    const goOnline = () => { setIsOnline(true); showToast('Back online — pending changes will sync', { type: 'success', duration: 4000 }); };
    const goOffline = () => { setIsOnline(false); showToast('You are offline — changes saved locally and will sync when reconnected', { type: 'warning', duration: 5000 }); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);



  return (
    <nav className="navbar">
      <div className="navbar__left">
        <button 
          className="navbar__menu-btn" 
          onClick={() => dispatch(toggleSidebar())}
          aria-label="Toggle sidebar"
        >
          <FaBars />
        </button>
        <h1 className="navbar__logo">MIVENT</h1>

        {/* Sync status button — shows sync state, opens Sync Center on click */}
        <span className="navbar__sync-wrapper">
          <SyncButton
            onClick={() => setShowSyncCenter(true)}
            showLabel={false}
            className="navbar__sync-btn"
          />
        </span>
      </div>

      {/* Sync Center modal */}
      <SyncCenter isOpen={showSyncCenter} onClose={() => setShowSyncCenter(false)} />

      {/* Offline info modal */}
      <Modal isOpen={showOfflineInfo} onClose={() => setShowOfflineInfo(false)} title="You're Offline" size="medium" className="modal--offline">
        <div className="offline-modal">
          <p className="offline-modal__text offline-modal__text--warning">
            This platform is designed for online use first. While offline mode is available, proceed with caution—some features may be delayed or require review after reconnecting.
          </p>
          <p className="offline-modal__text offline-modal__text--caution">
            You are offline—use this mode only for urgent edits. All other actions will sync later when online.
          </p>
          <p className="offline-modal__text offline-modal__text--info">
            You can create or add new data, but editing, updating, or deleting data is not available while offline.
          </p>
          <button className="offline-modal__dismiss" onClick={() => setShowOfflineInfo(false)}>Got it</button>
        </div>
      </Modal>

      <div className="navbar__right">
        {/* Plan button: shows tier-specific icon and label (none / silver / gold) */}
        <button
          className={`navbar__plan-btn ${showPlan ? 'navbar__plan-btn--active' : ''} ${isPlanExpired && !hasPaidPlan ? 'navbar__plan-btn--expired' : remainingDays <= 3 && !hasPaidPlan ? 'navbar__plan-btn--critical' : ''}`}
          data-tier={planTier}
          aria-label={`${planTitle}`}
          title={isPlanExpired && !hasPaidPlan ? 'Trial Expired - Click to Upgrade' : `${planTitle}`}
          onClick={() => setShowPlan(true)}
        >
          {isGold ? <GoldStar /> : isSilver ? <SilverStar /> : <FaPlusCircle />}
          <span className="navbar__plan-label">{planTitle}</span>
          {remainingDays <= 3 && !hasPaidPlan && (
            <span className="navbar__plan-badge">{isPlanExpired ? '!' : remainingDays}</span>
          )}
        </button>

          <Modal
            isOpen={showPlan}
            onClose={() => setShowPlan(false)}
            title="Your Plan"
            size="large"
            className="modal--plan"
          >
            <div className="plan-modal">
              <div className={`plan-modal__header ${isGold ? 'plan-modal__header--gold' : isSilver ? 'plan-modal__header--silver' : 'plan-modal__header--trial'} ${isPlanExpired || isPlanExpiringSoon ? 'plan-modal__header--danger' : ''}`}>
                <div className="plan-modal__header-left">
                  <div className="plan-modal__badge">
                    {isGold ? <GoldStar /> : isSilver ? <SilverStar /> : <FaPlusCircle />}
                  </div>
                  <div>
                    <h3 className="plan-modal__title">{planTitle}</h3>
                    <p className="plan-modal__subtitle">
                      {isGold 
                        ? 'Best for established teams' 
                        : isSilver 
                          ? 'Great value for growing teams' 
                          : isPlanExpired 
                            ? 'Your free trial has ended. Subscribe to continue.'
                            : `${remainingDisplay.short} in your free trial`
                      }
                    </p>
                  </div>
                </div>

                <div className="plan-modal__header-right">
                  <button
                    type="button"
                    className={`plan-modal__history-cta${isPlanExpired ? ' plan-modal__history-cta--expired' : isPlanExpiringSoon ? ' plan-modal__history-cta--soon' : ''}`}
                    onClick={() => setShowPaymentHistory(true)}
                    title={`View payment records — ${isPrivilegedUser ? 'lifetime access' : `expires ${planExpiryLabel}`}`}
                    aria-label="Open payment history"
                  >
                    <span className="plan-modal__history-info">
                      <span className="plan-modal__days">
                        <span className="plan-modal__days-count">{remainingDisplay.count}</span>
                        <span className="plan-modal__days-label">{remainingDisplay.label}</span>
                      </span>
                      <span className={`plan-modal__expiry${isPlanExpired ? ' plan-modal__expiry--expired' : isPlanExpiringSoon ? ' plan-modal__expiry--soon' : ''}`}>
                        <FaCalendarAlt className="plan-modal__expiry-icon" />
                        <span className="plan-modal__expiry-text">
                          {isPrivilegedUser ? (
                            <>Never expires</>
                          ) : (
                            <>
                              <span className="plan-modal__expiry-prefix">{isPlanExpired ? 'Expired on' : 'Expires on'}</span>
                              <strong className="plan-modal__expiry-date">{planExpiryLabel}</strong>
                            </>
                          )}
                        </span>
                      </span>
                    </span>
                    <span className="plan-modal__history-link">
                      <FaHistory /> <span className="plan-modal__history-text-full">Payment History</span><span className="plan-modal__history-text-short">History</span>
                    </span>
                  </button>
                </div>
              </div>

              <div className="plan-modal__body">
                {!hasPaidPlan ? (
                  <>
                    <p className="plan-modal__lead">
                      {isPlanExpired 
                        ? 'Choose a plan to unlock all features and continue your journey'
                        : 'Explore our plans — pick the one that suits your needs.'
                      }
                    </p>
                    <div className="plan-modal__options">
                      <PlanCard tier="Silver" />
                      <PlanCard tier="Gold" highlighted />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="plan-modal__centered">
                      <PlanCard tier={planTitle} highlighted={isGold} isCurrentPlan={true} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Modal>

          <PaymentHistoryModal
            isOpen={showPaymentHistory}
            onClose={() => setShowPaymentHistory(false)}
          />

        <div className="navbar__notifications">
          <NotificationPanel />
        </div>

        <button
          className="navbar__icon-btn navbar__icon-btn--tour"
          aria-label="Quick Tour"
          title="Quick Tour"
          onClick={() => setShowTutorial(true)}
        >
          <FaInfoCircle />
        </button>

        <button
          className="navbar__icon-btn"
          aria-label="Help"
          title="Help"
          onClick={() => setShowHelp(true)}
        >
          <FaQuestionCircle />
        </button>

        <a
          className="navbar__icon-btn"
          href="/support"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Support Site"
          title="Support & Documentation"
        >
          <FaHeadset />
        </a>

        <Modal
          isOpen={showTutorial}
          onClose={() => setShowTutorial(false)}
          title="Quick Tour"
          size="medium"
          className="modal--quick-tour"
          contentClassName="modal__content--tour"
          showCloseButton={false}
          headerActions={
            <button
              className="modal__close modal__close--tour"
              onClick={() => setShowTutorial(false)}
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          }
        >
          <div className="quick_tour">
            <div className="quick_tour__steps">
              <div className="quick_tour__step">
                <div className="quick_tour__step-num">1</div>
                <div className="quick_tour__step-body">
                  <strong>Dashboard</strong>
                  <span>Overview of recent activity and stats</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">2</div>
                <div className="quick_tour__step-body">
                  <strong>Quotes &amp; Bills</strong>
                  <span>Create quotations and invoices from the respective pages</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">3</div>
                <div className="quick_tour__step-body">
                  <strong>Projects &amp; Events</strong>
                  <span>Create and manage client projects and events</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">4</div>
                <div className="quick_tour__step-body">
                  <strong>Notes</strong>
                  <span>Create notes for your projects and events followup</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">5</div>
                <div className="quick_tour__step-body">
                  <strong>Service &amp; Policy</strong>
                  <span>Manage services and policies for clients</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">6</div>
                <div className="quick_tour__step-body">
                  <strong>Team</strong>
                  <span>Add team members and manage team payments</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">7</div>
                <div className="quick_tour__step-body">
                  <strong>Profile & Settings</strong>
                  <span>Manage your profile, company, payment and settings</span>
                </div>
              </div>

              <div className="quick_tour__step">
                <div className="quick_tour__step-num">8</div>
                <div className="quick_tour__step-body">
                  <strong>AI Assistant</strong>
                  <span>Utilize our AI-powered assistant for smarter workflows</span>
                </div>
              </div>
            </div>

            <div className="quick_tour__footer">
              <button
                className="quick_tour__watch-btn"
                onClick={async (e) => {
                  try {
                    let url = siteSettings?.youtubeUrl;
                    if (!url) {
                      const res = await fetch('/api/site-settings');
                      if (res.ok) {
                        const data = await res.json();
                        url = data.youtubeUrl || url;
                      }
                    }
                    if (!url) url = 'https://youtu.be/-VOkLeDl1WM?si=IsYwc2zUj0uD0dCd';
                    window.open(url, '_blank', 'noopener');
                  } catch (err) {
                    window.open('https://youtu.be/-VOkLeDl1WM?si=IsYwc2zUj0uD0dCd', '_blank', 'noopener');
                  }
                }}
              >
                <FaPlay /> Watch Tutorial Video
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          isOpen={showHelp}
          onClose={() => {
            setShowHelp(false);
            setHelpErrors({});
            setHelpSubmitting(false);
            setHelpSuccess(false);
          }}
          title="Help & Support"
          size="medium"
        >
          <div style={{ lineHeight: 1.5 }}>
            {/* Contact form */}
            <form
              id="help-form"
              className="navbar__help-form"
              onSubmit={async (e) => {
                e.preventDefault();
                // simple validation
                const errs = {};
                if (!helpForm.name.trim()) errs.name = 'Name is required';
                if (!helpForm.email.trim()) errs.email = 'Email is required';
                else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(helpForm.email)) errs.email = 'Enter a valid email';
                if (!helpForm.message.trim()) errs.message = 'Message is required';
                setHelpErrors(errs);
                if (Object.keys(errs).length) return;

                setHelpSubmitting(true);
                try {
                  const formData = new FormData(e.target);
                  // Override subject with a branded, well-formatted line
                  formData.set('subject', `[MIVENT Support] ${helpForm.subject.trim() || 'General Inquiry'} — from ${helpForm.name.trim()}`);
                  // Allow receiver to reply directly to the sender
                  formData.set('replyto', helpForm.email.trim());

                  const response = await fetch('https://api.web3forms.com/submit', {
                    method: 'POST',
                    body: formData,
                    credentials: 'omit',
                  });

                  const data = await response.json();
                  if (data.success) {
                    setHelpSuccess(true);
                    setHelpForm({ name: '', email: '', subject: '', message: '' });
                    e.target.reset();
                    // Automatically close success message after 10 seconds
                    setTimeout(() => {
                      setHelpSuccess(false);
                      setShowHelp(false);
                    }, 10000);
                  } else {
                    throw new Error(data.message || 'Submission failed');
                  }
                } catch (err) {
                  console.error('Web3Forms Error:', err);
                  setHelpErrors({ form: 'Unable to send message. Please email miventsite@gmail.com' });
                } finally {
                  setHelpSubmitting(false);
                }
              }}
            >
              {/* Hidden Web3Forms control fields */}
              <input type="hidden" name="access_key" value="73633d23-2454-4901-bea6-999b5a352e90" />
              <input type="hidden" name="from_name" value="MIVENT Platform" />
              {/* Honeypot spam prevention */}
              <input type="checkbox" name="botcheck" style={{ display: 'none' }} />

              {helpSuccess ? (
                <div className="help-success">
                  <h4>Thanks — we received your message</h4>
                  <p>Our support team will get back to you at the email address you provided (usually within 24 hours).</p>
                </div>
              ) : (
                <>
                  <div className="help-form__row">
                    <div className="help-form__group">
                      <label htmlFor="help-name">Name</label>
                      <input
                        id="help-name"
                        name="Sender Name"
                        value={helpForm.name}
                        onChange={(e) => setHelpForm((s) => ({ ...s, name: e.target.value }))}
                        className="help-form__input"
                        placeholder="Your full name"
                      />
                      {helpErrors.name && <div className="help-form__error">{helpErrors.name}</div>}
                    </div>

                    <div className="help-form__group">
                      <label htmlFor="help-email">Email</label>
                      <input
                        id="help-email"
                        name="Sender Email"
                        value={helpForm.email}
                        onChange={(e) => setHelpForm((s) => ({ ...s, email: e.target.value }))}
                        className="help-form__input"
                        placeholder="you@company.com"
                      />
                      {helpErrors.email && <div className="help-form__error">{helpErrors.email}</div>}
                    </div>
                  </div>

                  <div className="help-form__group">
                    <label htmlFor="help-subject">Subject (optional)</label>
                    <input
                      id="help-subject"
                      name="Topic"
                      value={helpForm.subject}
                      onChange={(e) => setHelpForm((s) => ({ ...s, subject: e.target.value }))}
                      className="help-form__input"
                      placeholder="Short summary of your issue"
                    />
                  </div>

                  <div className="help-form__group">
                    <label htmlFor="help-message">Message</label>
                    <textarea
                      id="help-message"
                      name="Message"
                      value={helpForm.message}
                      onChange={(e) => setHelpForm((s) => ({ ...s, message: e.target.value }))}
                      className="help-form__textarea"
                      rows={6}
                      placeholder="Describe the issue or question in detail"
                    />
                    {helpErrors.message && <div className="help-form__error">{helpErrors.message}</div>}
                  </div>

                  {helpErrors.form && <div className="help-form__error help-form__error--form">{helpErrors.form}</div>}

                  <div className="help-form__actions">
                    <button type="submit" className="help-form__submit" disabled={helpSubmitting}>
                      {helpSubmitting ? 'Sending…' : 'Send Message'}
                    </button>
                  </div>
                </>
              )}
            </form>

            {/* Contact details */}
            <div className="help-contact-info" style={{ marginTop: 18 }}>
              <h4>Other ways to contact us</h4>
              <div className="help-contact-channels">
                <a
                  className="help-contact-channel help-contact-channel--whatsapp"
                  href="https://wa.me/917676572364"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat on WhatsApp"
                  title="WhatsApp"
                >
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </a>
                <a
                  className="help-contact-channel help-contact-channel--mail"
                  href="mailto:miventsite@gmail.com"
                  aria-label="Send an email"
                  title="Email"
                >
                  <FaEnvelope />
                  <span>Email</span>
                </a>
                <a
                  className="help-contact-channel help-contact-channel--instagram"
                  href="https://www.instagram.com/miventsite/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow on Instagram"
                  title="Instagram"
                >
                  <FaInstagram />
                  <span>Instagram</span>
                </a>
                <a
                  className="help-contact-channel help-contact-channel--youtube"
                  href="https://www.youtube.com/@MIVENTSITE"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Watch on YouTube"
                  title="YouTube"
                >
                  <FaYoutube />
                  <span>YouTube</span>
                </a>
              </div>
              <p>Response time: Within 24 hours (business hours)</p>
              <a href="/support#feedback" target="_blank" rel="noopener noreferrer" className="help-contact-feedback">
                Send Feedback
              </a>
            </div>
          </div>
        </Modal>

        <div className="navbar__user">
          <button
            className={`navbar__profile-btn ${pathname.startsWith('/settings') ? 'navbar__profile-btn--active' : ''}`}
            onClick={() => navigate('/settings')}
            aria-label="Open settings"
          >
            {displayAvatar ? (
              <img src={displayAvatar} alt="Profile" className="navbar__profile-img" />
            ) : (
              <FaUserCircle />
            )}
            <span>{displayName}</span>
          </button>
        </div>
      </div>

      {/* Offline info modal */}
      <Modal isOpen={showOfflineInfo} onClose={() => setShowOfflineInfo(false)} title="You're Offline" size="medium" className="modal--offline">
        <div className="offline-modal">
          <p className="offline-modal__text offline-modal__text--warning">
            This platform is designed for online use first. While offline mode is available, proceed with caution—some features may be delayed or require review after reconnecting.
          </p>
          <p className="offline-modal__text offline-modal__text--caution">
            You are offline—use this mode only for urgent edits. All other actions will sync later when online.
          </p>
          <p className="offline-modal__text offline-modal__text--info">
            You can create or add new data, but editing, updating, or deleting data is not available while offline.
          </p>
          <button className="offline-modal__dismiss" onClick={() => setShowOfflineInfo(false)}>Got it</button>
        </div>
      </Modal>

      {/* Offline badge remains in App.js for bottom center positioning */}
    </nav>
  );
};

export default Navbar;
